/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import { EntityChange } from '../types/EntityChange';
import { makeRequest } from '../api/makeRequest';
import { RequestStatus } from '../types/RequestStatus';
import * as logger from '../logging/logger';
import * as events from '../messaging/events';

export interface CrudOperationsActions<T> {
    create: (item: T) => void;
    read: (query?: string) => void;
    update: (item: T, options?: { updateBeforeResponse?: boolean }) => void;
    delete: (item: T) => void;
    post: (items: T[]) => void;
    setData: (items: T[]) => void;
}

export interface CrudOperationsStatus {
    create: RequestStatus;
    read: RequestStatus;
    update: RequestStatus;
    delete: RequestStatus;
    post: RequestStatus;
}

interface CrudMetadata {
    totalResults: number;
}

export interface CrudOptions {
    onCreateSuccess?: () => void;
    onUpdateSuccess?: () => void;
    onDeleteSuccess?: () => void;
}

export function useCrudOperations<
    T extends {
        id?: number;
    }
>(url: string, mqttChannel?: string, crudOptions?: CrudOptions): [T[], CrudOperationsActions<T>, CrudOperationsStatus, CrudMetadata] {
    const [items, setData] = useState<T[]>([]);
    const [metadata, setMetadata] = useState<CrudMetadata>({ totalResults: 0 });
    const [readRequestStatus, setReadRequestStatus] = useState<RequestStatus>({ isPending: false, hasError: false });

    const initialStatus = { isPending: false, hasError: false };
    const [updateRequestStatus, setUpdateRequestStatus] = useState<RequestStatus>(initialStatus);
    const [createRequestStatus, setCreateRequestStatus] = useState<RequestStatus>(initialStatus);
    const [deleteRequestStatus, setDeleteRequestStatus] = useState<RequestStatus>(initialStatus);
    const [postRequestStatus, setPostRequestStatus] = useState<RequestStatus>(initialStatus);

    const read = useCallback(
        async (query?: string) => {
            try {
                setReadRequestStatus({ isPending: true, hasError: false });
                const response = await makeRequest({
                    method: 'GET',
                    path: query ? `${url}?${query}` : url
                });

                let items: T[];

                if (response && response.payload && Array.isArray(response.payload)) {
                    items = response.payload;
                    setMetadata({ totalResults: response.count });
                } else if (response && response.items && Array.isArray(response.items)) {
                    items = response.items;
                    setMetadata({ totalResults: response.count });
                } else {
                    items = response;
                }

                setData(items);
                setReadRequestStatus({ isPending: false, hasError: false });
            } catch (err) {
                setReadRequestStatus({ isPending: false, hasError: true });
                logger.log('Error', `useCrudOperations: Error: ${err.message}`);
            }
        },
        [url]
    );

    const create = useCallback(
        async (item: T) => {
            try {
                setCreateRequestStatus({ isPending: true, hasError: false });
                const response = await makeRequest({
                    method: 'POST',
                    path: url,
                    data: item
                });

                let newItem: T;
                if (response && response.payload) {
                    newItem = response.payload as T;
                } else {
                    newItem = response as T;
                }
                addItemToState(newItem);
                setCreateRequestStatus({ isPending: false, hasError: false });
                crudOptions?.onCreateSuccess && crudOptions.onCreateSuccess();
            } catch (err) {
                setCreateRequestStatus({ isPending: false, hasError: true });
                logger.log('Error', `useCrudOperations: Error: ${err.message}`);
            }
        },
        [items, url]
    );

    const update = useCallback(
        async (item: T, options?) => {
            try {
                const updateBeforeResponse = options && options.updateBeforeResponse;

                /* istanbul ignore next */
                if (updateBeforeResponse) {
                    setItemInState(item);
                }

                setUpdateRequestStatus({ isPending: true, hasError: false });
                await makeRequest({
                    method: 'PUT',
                    path: `${url}/${item.id}`,
                    data: item
                });

                /* istanbul ignore next */
                if (!updateBeforeResponse) {
                    setItemInState(item);
                }
                setUpdateRequestStatus({ isPending: false, hasError: false });
                crudOptions?.onUpdateSuccess && crudOptions.onUpdateSuccess();
            } catch (err) {
                setUpdateRequestStatus({ isPending: false, hasError: true });
                logger.log('Error', `useCrudOperations: Error: ${err.message}`);
            }
        },
        [items]
    );

    const post = useCallback(
        async (items: T[]) => {
            try {
                setPostRequestStatus({ isPending: true, hasError: false });
                await makeRequest({
                    method: 'POST',
                    path: `${url}`,
                    data: items
                });

                setData(items);
                setPostRequestStatus({ isPending: false, hasError: false });
            } catch (err) {
                setPostRequestStatus({ isPending: false, hasError: true });
                logger.log('Error', `useCrudOperations: Error: ${err.message}`);
            }
        },
        [items]
    );

    const deleteItem = useCallback(
        async (item: T) => {
            try {
                setDeleteRequestStatus({ isPending: true, hasError: false });

                await makeRequest({
                    method: 'DELETE',
                    path: `${url}/${item.id}`
                });

                deleteItemFromState(item);
                setDeleteRequestStatus({ isPending: false, hasError: false });
                crudOptions?.onDeleteSuccess && crudOptions.onDeleteSuccess();
            } catch (err) {
                setDeleteRequestStatus({ isPending: false, hasError: true });
                logger.log('Error', `useCrudOperations: Error: ${err.message}`);
            }
        },
        [items]
    );

    const addItemToState = useCallback(
        (item: T) => {
            const newItems = [] as T[];
            setData(newItems.concat(item, items) as T[]);
        },
        [items]
    );

    const setItemInState = useCallback(
        (item: T) => {
            setData(items.map(x => (x.id === item.id ? item : x)));
        },
        [items]
    );

    const deleteItemFromState = useCallback(
        (item: T) => {
            setData(items.filter(x => x.id !== item.id));
        },
        [items]
    );

    useEffect(() => {
        if (mqttChannel) {
            events.subscribe(mqttChannel);
        }

        return () => {
            if (mqttChannel) {
                events.unsubscribe(mqttChannel);
            }
        };
    }, []);

    useEffect(() => {
        const handleMessage = (json: EntityChange) => {
            const item = json.entityChanged as T;

            switch (json.changeType) {
                case 'Created':
                    addItemToState(item);
                    break;

                case 'Updated':
                    setItemInState(item);
                    break;

                case 'Deleted':
                    deleteItemFromState(item);
                    break;
            }
        };

        if (mqttChannel) {
            events.on(mqttChannel, handleMessage);
        }

        return () => {
            if (mqttChannel) {
                events.off(mqttChannel, handleMessage);
            }
        };
    }, [items]);

    return [
        items,
        {
            create,
            read,
            update,
            delete: deleteItem,
            post,
            setData
        },
        {
            create: createRequestStatus,
            read: readRequestStatus,
            update: updateRequestStatus,
            delete: deleteRequestStatus,
            post: postRequestStatus
        },
        metadata
    ];
}
